import { Keyboard } from "lucide-react";
import { useTranslate } from "ra-core";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  PROMPT_MS,
  actionOfKey,
  drawRound,
  hotkeyScore,
  type HotkeyAnswer,
  type HotkeyPrompt,
} from "../content/hotkeys";
import { GameFrame } from "./GameFrame";

/** The dialer's own help list (gavion.dialer.hotkeys.*), shown before the round. */
const DIALER_HELP = [
  "outcomes",
  "skip",
  "note",
  "save",
  "space",
  "objections",
  "help",
] as const;

export type HotkeyResult = {
  score: number;
  correct: number;
  total: number;
  /** Mean answer time of the right answers, in ms (null: none right). */
  avgMs: number | null;
};

type Feedback = { kind: "hit" | "miss" | "timeout"; keyLabel: string };

/**
 * « Doigts de feu »: a timed round of dialer shortcuts. The keyboard is listened to ONLY while a round runs (window
 * listener added on start, removed on finish / quit / unmount); keys the dialer ignores (Tab, arrows…) pass
 * through untouched. Esc gives up the round.
 */
export const HotkeyGame = ({
  best,
  onFinish,
  onQuit,
  rng = Math.random,
  clock = () => performance.now(),
  promptMs = PROMPT_MS,
  feedbackMs = 700,
}: {
  best: number | null;
  onFinish: (r: HotkeyResult) => void;
  onQuit: () => void;
  rng?: () => number;
  clock?: () => number;
  promptMs?: number;
  feedbackMs?: number;
}) => {
  const t = useTranslate();
  const [playing, setPlaying] = useState(false);
  const [prompts, setPrompts] = useState<HotkeyPrompt[]>([]);
  const [index, setIndex] = useState(0);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const answers = useRef<HotkeyAnswer[]>([]);
  const startedAt = useRef(0);
  const areaRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLButtonElement>(null);
  const prompt = prompts[index];

  const keyText = (label: string) =>
    label.startsWith("gavion.") ? t(label) : label;

  const start = () => {
    answers.current = [];
    setPrompts(drawRound(rng));
    setIndex(0);
    setFeedback(null);
    setPlaying(true);
    startedAt.current = clock();
  };
  const stop = useCallback(() => {
    setPlaying(false);
    setFeedback(null);
  }, []);

  const answer = useCallback(
    (a: HotkeyAnswer, kind: Feedback["kind"]) => {
      if (!prompt) return;
      answers.current = [...answers.current, a];
      setFeedback({ kind, keyLabel: prompt.keyLabel });
    },
    [prompt],
  );

  useEffect(() => {
    if (playing) areaRef.current?.focus();
    else startRef.current?.focus();
  }, [playing]);

  // time limit of the current prompt
  useEffect(() => {
    if (!playing || feedback) return;
    const id = window.setTimeout(
      () => answer({ correct: false, ms: promptMs }, "timeout"),
      promptMs,
    );
    return () => window.clearTimeout(id);
  }, [playing, feedback, index, promptMs, answer]);

  // after the feedback: next prompt, or the end of the round
  useEffect(() => {
    if (!playing || !feedback) return;
    const id = window.setTimeout(() => {
      if (index + 1 >= prompts.length) {
        const all = answers.current;
        const right = all.filter((x) => x.correct);
        setPlaying(false);
        setFeedback(null);
        onFinish({
          score: hotkeyScore(all),
          correct: right.length,
          total: all.length,
          avgMs: right.length
            ? right.reduce((s, x) => s + x.ms, 0) / right.length
            : null,
        });
        return;
      }
      setIndex((i) => i + 1);
      setFeedback(null);
      startedAt.current = clock();
    }, feedbackMs);
    return () => window.clearTimeout(id);
  }, [playing, feedback, index, prompts.length, feedbackMs, clock, onFinish]);

  // the keyboard belongs to the game only while a round runs (layout effect: listening as soon as the prompt shows)
  useLayoutEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      if (e.key === "Escape") {
        e.preventDefault();
        stop();
        return;
      }
      const action = actionOfKey(e);
      if (action === null) return;
      e.preventDefault();
      if (feedback || !prompt) return;
      const right = action === prompt.action;
      answer(
        { correct: right, ms: Math.max(0, clock() - startedAt.current) },
        right ? "hit" : "miss",
      );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing, feedback, prompt, answer, clock, stop]);

  if (!playing)
    return (
      <GameFrame
        step={t("gavion.training.missions.hotkeys.title")}
        current={0}
        total={1}
        onQuit={onQuit}
      >
        <p className="text-sm">
          {t("gavion.training.hotkeys.intro", {
            seconds: Math.round(promptMs / 1000),
          })}
        </p>
        <div className="rounded-lg border px-3 py-2">
          <h2 className="mb-1 flex items-center gap-2 text-sm font-bold">
            <Keyboard className="size-4 text-primary" aria-hidden="true" />
            {t("gavion.training.hotkeys.keys_title")}
          </h2>
          <ul className="grid gap-x-4 gap-y-0.5 text-sm sm:grid-cols-2">
            {DIALER_HELP.map((k) => (
              <li key={k}>{t(`gavion.dialer.hotkeys.${k}`)}</li>
            ))}
          </ul>
        </div>
        {best !== null && (
          <p className="text-sm font-semibold">
            {t("gavion.training.hotkeys.record", { score: best })}
          </p>
        )}
        <Button ref={startRef} size="lg" onClick={start} className="self-start">
          {t("gavion.training.hotkeys.start")}
        </Button>
      </GameFrame>
    );

  return (
    <GameFrame
      step={t("gavion.training.hotkeys.progress", {
        n: index + 1,
        total: prompts.length,
      })}
      current={index + (feedback ? 1 : 0)}
      total={prompts.length}
      onQuit={stop}
    >
      <div
        ref={areaRef}
        tabIndex={-1}
        role="group"
        aria-label={t("gavion.training.hotkeys.area")}
        className="flex flex-col items-center gap-4 rounded-xl bg-accent/60 px-4 py-8 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {t("gavion.training.hotkeys.press")}
        </p>
        <p className="text-xl font-extrabold sm:text-2xl" aria-live="assertive">
          {prompt ? t(prompt.promptKey) : ""}
        </p>
        <div className="h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-primary/15">
          {!feedback && (
            <div
              key={index}
              data-testid="hotkey-countdown"
              className="h-full origin-left rounded-full bg-primary motion-reduce:hidden"
              style={{
                animation: `gavion-countdown ${promptMs}ms linear forwards`,
              }}
            />
          )}
        </div>
        <p
          role="status"
          className={cn(
            "min-h-6 text-sm font-bold",
            feedback?.kind === "hit"
              ? "text-green-700 dark:text-green-400"
              : "text-destructive",
          )}
        >
          {feedback?.kind === "hit" && t("gavion.training.hotkeys.hit")}
          {feedback?.kind === "miss" &&
            t("gavion.training.hotkeys.miss", {
              key: keyText(feedback.keyLabel),
            })}
          {feedback?.kind === "timeout" &&
            t("gavion.training.hotkeys.timeout", {
              key: keyText(feedback.keyLabel),
            })}
        </p>
      </div>
      <Button variant="outline" onClick={stop} className="self-end">
        {t("gavion.training.hotkeys.quit")}
      </Button>
    </GameFrame>
  );
};
